import { useEffect, useRef } from 'react'

import { useLoadUrl } from '@cash-web/shared-data-access-load-url'
import { BlockerPlugin, ClientRoutesPlugin } from '@cash-web/shared-echo-util-plugin'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'

import { useEchoPluginsSafe } from './EchoPluginProvider'

/**
 * Bridge component that registers Echo plugins that need journey/navigation access.
 */
export const EchoJourneyBridge = () => {
  const loadUrl = useLoadUrl()
  const journey = useJourneyContext()
  const clientRoutesPluginRef = useRef<ClientRoutesPlugin | null>(null)
  const blockerPluginRef = useRef<BlockerPlugin | null>(null)
  const echoContext = useEchoPluginsSafe()

  useEffect(() => {
    if (!echoContext) return

    const { client, registerPlugin, unregisterPlugin } = echoContext
    if (!client) return

    const clientRoutesPlugin = new ClientRoutesPlugin(client, {
      onNavigate: (path: string) => {
        loadUrl({ url: `https://internal.cash.app${path}`, trackingName: 'EchoJourneyBridge' })
      },
    })
    clientRoutesPluginRef.current = clientRoutesPlugin
    registerPlugin(clientRoutesPlugin)

    const blockerPlugin = new BlockerPlugin(client, {
      onLaunchBlocker: (payload: string) => {
        try {
          const blockerDescriptor = JSON.parse(payload)

          journey.navigate('/web/flow/blocker-descriptors', {
            state: {
              blockerDescriptors: [blockerDescriptor],
            },
          })
        } catch (error) {
          client.logger.error('[EchoJourneyBridge] Error parsing blocker payload:', error)
        }
      },
    })
    blockerPluginRef.current = blockerPlugin
    registerPlugin(blockerPlugin)

    return () => {
      if (clientRoutesPluginRef.current) {
        unregisterPlugin(clientRoutesPluginRef.current.id)
      }
      if (blockerPluginRef.current) {
        unregisterPlugin(blockerPluginRef.current.id)
      }
    }
  }, [echoContext, loadUrl, journey])

  return null
}
